<template>
  <div class="product-modal" itemscope itemtype="http://schema.org/Product">

    <!-- Breadcrumbs -->
    <nav class="product-modal-nav">
      <breadcrumbs :with-homepage="true"
                   :routes="breadcrumbs.routes"
                   :active-route="breadcrumbs.name"
      />
    </nav>
    <!---->

    <!-- Content -->
    <div class="product-modal-content">

      <!-- Content image -->
      <div class="product-modal-content-picture image">
        <product-gallery
          :offline="image"
          :gallery="gallery"
          :configuration="configuration"
          :product="product"
        />
      </div>
      <!---->

      <!-- Content details -->
      <div class="product-modal-content-details">
        <h1 class="product-modal-content-details-title" data-testid="productName" itemprop="name">
          {{ product.name | htmlDecode }}
          <web-share class="web-share"
                     :title="product.name | htmlDecode"
                     text="Check this product!"
          />
        </h1>
        <h3 class="product-modal-content-details-subtitle"> {{ $t('SKU') }}: <strong itemprop="sku"> {{ product.sku }} </strong> </h3>
        <meta itemprop="category" :content="schemaProductCategory">
        <div itemprop="brand" itemscope itemtype="http://schema.org/Brand" v-if="brandName">
          <meta itemprop="name" :content="brandName">
        </div>
        <meta itemprop="image" :content="galleryImage.src" v-for="galleryImage in gallery">

        <!-- Qty -->
        <div class="product-modal-content-details-qty" :class="{'disabled': isDisabledProduct}" itemprop="offers" itemscope itemtype="http://schema.org/Offer">
          <meta itemprop="priceCurrency" :content="currentStore.i18n.currencyCode">
          <meta itemprop="price" :content="parseFloat(product.priceInclTax).toFixed(2)">
          <meta itemprop="availability" :content="structuredData.availability">
          <meta itemprop="url" :content="`${getHostPath}${productLink}`">
          <meta itemprop="priceValidUntil" :content="priceValidUntil" v-if="priceValidUntil">
          <div itemprop="shippingDetails" itemscope itemtype="http://schema.org/OfferShippingDetails" v-if="shippingRateAmount !== null">
            <div itemprop="shippingRate" itemscope itemtype="http://schema.org/MonetaryAmount">
              <meta itemprop="value" :content="shippingRateAmount">
              <meta itemprop="currency" :content="currentStore.i18n.currencyCode">
            </div>
            <div itemprop="shippingDestination" itemscope itemtype="http://schema.org/DefinedRegion">
              <meta itemprop="addressCountry" content="UA">
            </div>
          </div>

          <!-- Qty inputs -->
          <div class="product-modal-content-details-qty-container" v-if="product.type_id !== 'grouped' && product.type_id !== 'bundle' && !isDisabledProduct">
            <base-input-number
              style="width: auto"
              v-if="!product.stock.is_qty_decimal"
              :name="$t('Quantity')"
              v-model="product.qty"
              :min="product.stock.min_sale_qty"
              :qty="product.stock.qty"
              :sku="product.sku"
              :qty-in-cart="qtyInCart"
              :on-disable="onDisable"
              @blur="$v.$touch()"
              @refresh="refresh"
              :validations="[
                {
                  condition: $v.product.qty.$error && !$v.product.qty.minValue,
                  text: $t('Quantity must be above 0')
                }
              ]"
            />
            <base-input-decimal-number
              style="width: auto"
              v-else
              :name="$t('Quantity')"
              v-model="product.qty"
              :min="product.stock.min_sale_qty"
              :qty="product.stock.qty"
              :sku="product.sku"
              :qty-increments="product.stock.qty_increments"
              :qty-in-cart="qtyInCart"
              :on-disable="onDisable"
              @blur="$v.$touch()"
              @refresh="refresh"
              :validations="[
                {
                  condition: $v.product.qty.$error && !$v.product.qty.minValue,
                  text: $t('Quantity must be above 0')
                }
              ]"
            />
            <span class="product-modal-content-details-qty-container-value">
              <price :product="product" :qty="product.qty" :final-price="true" />
            </span>
          </div>
          <!---->

          <!-- Qty price -->
          <div class="product-modal-content-details-qty-container mt10" :class="{'disabled': isDisabledProduct}">
            <price :product="product" :qty="1" />
          </div>
          <!---->

          <!-- Weight -->
          <div class="pb-0 weight" v-if="product.weight">
            <span>{{ $t('Weight') }}:</span>
            <span itemprop="weight">{{ product.weight | toWeight }}</span>
          </div>
          <!---->

          <p class="m0 cl-secondary medium disabled-info" v-if="isDisabledProduct">{{ $t('The product is temporarily unavailable') }}</p>
        </div>
        <!---->

        <!-- Add to cart button -->
        <add-to-cart
          :product="product"
          v-if="isCheckoutAllowed && activeButtonAddToCart && !isDisabledProduct"
          :disabled="($v.product.qty.$error && !$v.product.qty.minValue) || onDisable"
          class="product-modal-content-details-addtocart"
        />
        <!---->

      </div>
      <!---->

    </div>
    <!---->

    <!-- Information -->
    <div class="product-modal-info">
      <product-tab v-if="isIngredientsExist" :height="getTableHeight">
        <h1 class="product-modal-info-title" slot="title"> {{ $t('Ingredients') }} </h1>
        <table class="product-modal-info-table" slot="content">
          <tr v-if="product.calories">
            <td> {{ $t('Calories') }}: </td>
            <td> <strong> {{ product.calories }} </strong> </td>
          </tr>
          <tr v-if="product.fat">
            <td> {{ $t('Fat') }}: </td>
            <td> <strong> {{ product.fat }} </strong> </td>
          </tr>
          <tr v-if="product.protein">
            <td> {{ $t('Protein') }}: </td>
            <td> <strong> {{ product.protein }} </strong> </td>
          </tr>
          <tr v-if="product.carbohydrates">
            <td> {{ $t('Carbohydrates') }}: </td>
            <td> <strong> {{ product.carbohydrates }} </strong> </td>
          </tr>
          <tr v-if="product.weight">
            <td> {{ $t('Weight') }}: </td>
            <td> <strong> {{ product.weight }} </strong> </td>
          </tr>
          <tr v-if="product.trade_mark">
            <td> {{ $t('Trade mark') }}: </td>
            <td> <strong> {{ product.trade_mark }} </strong> </td>
          </tr>
        </table>
      </product-tab>
      <product-tab v-if="product.description || product.weight">
        <h1 class="product-modal-info-title" slot="title"> {{ $t('Description') }} </h1>
        <div class="product-modal-info-wrapper" slot="content" itemprop="description">
          <p class="product-modal-info-text" slot="content" v-html="product.description"></p>
        </div>
      </product-tab>
    </div>
    <!---->

    <!-- Slider -->
    <div class="product-modal-slider">
      <product-related type="related" />
    </div>
    <!---->

    <!-- Size -->
    <SizeGuide />
    <!---->

  </div>
</template>

<script>
import ProductRelated from 'theme/components/core/blocks/Shop/Product/ProductRelated'
import ProductTab from 'theme/components/core/blocks/Shop/Product/ProductTab'
import Product from '@vue-storefront/core/pages/Product'
import VueOfflineMixin from 'vue-offline/mixin'
import RelatedProducts from 'theme/components/core/blocks/Product/Related.vue'
import Price from 'theme/components/core/blocks/Product/Price.vue'
import AddToCart from 'theme/components/core/AddToCart.vue'
import GenericSelector from 'theme/components/core/GenericSelector'
import ColorSelector from 'theme/components/core/ColorSelector.vue'
import SizeSelector from 'theme/components/core/SizeSelector.vue'
import Breadcrumbs from 'theme/components/core/Breadcrumbs.vue'
import ProductAttribute from 'theme/components/core/ProductAttribute.vue'
import ProductLinks from 'theme/components/core/ProductLinks.vue'
import ProductCustomOptions from 'theme/components/core/ProductCustomOptions.vue'
import ProductBundleOptions from 'theme/components/core/ProductBundleOptions.vue'
import ProductGallery from 'theme/components/core/ProductGallery'
import focusClean from 'theme/components/theme/directives/focusClean'
import WebShare from '@vue-storefront/core/modules/social-share/components/WebShare'
import BaseInputNumber from 'theme/components/core/blocks/Form/BaseInputNumber'
import BaseInputDecimalNumber from 'theme/components/core/blocks/Form/BaseInputDecimalNumber'
import SizeGuide from 'theme/components/core/blocks/Product/SizeGuide'
import config from 'config'
import { minValue } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import { canShowSpecialPriceForProduct } from '@vue-storefront/core/modules/catalog/helpers/tax'
import moment from 'moment'

export default {
  components: {
    'WishlistButton': () => import(/* webpackChunkName: "wishlist" */'theme/components/core/blocks/Wishlist/AddToWishlist'),
    AddToCart,
    Breadcrumbs,
    ColorSelector,
    GenericSelector,
    ProductAttribute,
    ProductBundleOptions,
    ProductCustomOptions,
    ProductGallery,
    ProductLinks,
    RelatedProducts,
    Price,
    SizeSelector,
    WebShare,
    BaseInputNumber,
    BaseInputDecimalNumber,
    SizeGuide,
    ProductTab,
    ProductRelated
  },
  mixins: [Product, VueOfflineMixin],
  data () {
    return {
      detailsOpen: false,
      currentQty: 0,
      showDetailsWrapper: true
    }
  },
  directives: { focusClean },
  computed: {
    ...mapGetters({
      store: 'partners/getCurrentPartner',
      currentLocation: 'locations/getActiveLocation',
      currentPartner: 'partners/getCurrentPartner'
    }),
    isCheckoutAllowed () {
      return (this.currentLocation.hasOwnProperty('is_checkout_allowed')) ? this.currentLocation.is_checkout_allowed : true
    },
    // K2-3036: see Product.vue's identical computed for why this now also folds in
    // isDisabledProduct rather than just the partner half of it.
    activeButtonAddToCart () {
      if (!this.currentPartner) return false
      return this.currentPartner.active && this.currentPartner.enabled && !this.isDisabledProduct
    },
    structuredData () {
      return {
        availability: (this.product.stock.is_in_stock && !this.isDisabledProduct) ? 'InStock' : 'OutOfStock'
      }
    },
    schemaProductCategory () {
      // K2-3027 follow-up: see Product.vue's identical computed for why this
      // joins the full path instead of trusting product.category's last
      // element as the leaf/primary category.
      if (this.product && this.product.category && this.product.category.length > 0) {
        return this.product.category
          .map(category => category && category.name)
          .filter(name => name && name !== 'null')
          .join(' > ')
      }
      return ''
    },
    brandName () {
      return (this.product && this.product.trade_mark) || (this.currentPartner && this.currentPartner.title) || ''
    },
    priceValidUntil () {
      if (canShowSpecialPriceForProduct(this.product) && this.product.special_to_date) {
        return moment(this.product.special_to_date).format('YYYY-MM-DD')
      }
      return null
    },
    shippingRateAmount () {
      return (this.currentLocation && this.currentLocation.base_delivery_price)
        ? parseFloat(this.currentLocation.base_delivery_price).toFixed(2)
        : null
    },
    cartItem () {
      return this.$store.getters['cart/getCartItems'].find(item => item.entity_id === this.product.entity_id)
    },
    qtyInCart () {
      if (this.cartItem) {
        return parseFloat(this.cartItem.qty).toFixed(2)
      }
    },
    onDisable () {
      let correctQtyInCart = parseFloat(this.qtyInCart)
      let currentQty = this.product.stock.qty - correctQtyInCart
      currentQty = currentQty.toFixed(2)
      if (currentQty <= 0.1) return true
      else return false
    },
    isIngredientsExist () {
      return this.ingredientsArr.length
    },
    ingredientsArr () {
      let ingredients = []
      let productIngredients =[
        this.product.calories,
        this.product.fat,
        this.product.protein,
        this.product.carbohydrates,
        this.product.weight,
        this.product.trade_mark
      ]
      if (productIngredients.length) {
        productIngredients.map(el => typeof el !== 'undefined' && ingredients.push(el))
      }
      return ingredients
    },
    getTableHeight () {
      let counter = 0
      this.ingredientsArr.map(el => el && counter++)
      return (counter * 28) + ((counter - 1) * 3) // 28px - height of <td>, 3px - gap height in table
    }
  },
  methods: {
    ...mapActions({
      cleanUpCategory: 'category/cleanUpCategory'
    }),
    showDetails (event) {
      this.detailsOpen = true
      event.target.classList.add('hidden')
    },
    notifyOutStock () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: this.$t('The product is out of stock and cannot be added to the cart!'),
        action1: { label: this.$t('OK') }
      })
    },
    notifyWrongAttributes () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'warning',
        message: this.$t('No such configuration for the product. Please do choose another combination of attributes.'),
        action1: { label: this.$t('OK') }
      })
    },
    openSizeGuide () {
      this.$bus.$emit('modal-show', 'modal-sizeguide')
    },
    refresh (event) {
      let newQty = event.newValue
      this.product.qty = newQty
    },
    getServiceText (service, store) {
      return (service.code === 'pickup' || service.code === 'minimal_order_amount')
        ? service.title
        : `${service.title}: ${store.working_hours}`
    }
  },
  beforeDestroy () {
    if (this.$route.name !== 'seo-category') {
      this.cleanUpCategory()
    }
  },
  validations: {
    product: {
      qty: {
        minValue: minValue(config.restrictions.productMinQty.isDecimal)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.pb-0 {
  padding-bottom: 0;
}

.weight {
  color: #4F4F4F;
  font-size: 16px;
}

.disabled-info {
  font-size: 18px;
}

.product-modal {
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;

  &-nav {
    margin-bottom: 20px;

    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    &-close {
      width: 20px;
      height: 20px;
      margin-left: 40px;

      background-image: url('../../../../../assets/shop/close.svg');
      background-position: center;
      background-size: 15px;
      background-repeat: no-repeat;

      background-color: transparent;
      border: none;
      outline: none;
      transition: .2s;

      &:hover {
        cursor: pointer;
        transform: rotate(90deg);
      }
    }
  }

  &-content {
    display: flex;
    justify-content: space-between;

    @media only screen and (max-width: 768px) {
      flex-direction: column;
    }

    &-picture {
      width: 345px;
      height: 345px;
      margin-right: 30px;

      @media only screen and (max-width: 768px) {
        width: 100%;
        height: 100%;
        margin-right: 0;
      }
    }

    &-details {
      width: 50%;

      display: flex;
      flex-direction: column;

      @media only screen and (max-width: 768px) {
        width: 100%;
        margin-top: 20px;
      }

      &-title {
        margin: 0 0 10px 0;
        padding: 0;

        color: #111;
        font-size: 18px;
        font-weight: 600;
      }

      &-subtitle {
        margin: 0;
        padding: 0;

        color: #9b9b9b;
        font-size: 14px;
        font-weight: 400;

        & strong {
          color: #111;
        }
      }

      &-qty {
        margin-top: 10px;
        padding: 10px 0;

        border-top: 1px solid #f9f8f6;
        border-bottom: 1px solid #f9f8f6;

        &.disabled {
          padding-top: 0;
        }

        &-container {
          display: flex;
          align-items: center;

          &-value {
            padding-left: 20px;
            margin-left: 20px;
            position: relative;

            color: #0b0b0b;
            font-size: 18px;
            font-weight: 600;

            &:before {
              width: 1px;
              height: 100%;
              margin: auto;

              position: absolute;
              top: 0;
              left: 0;
              bottom: 0;
              content: '';

              background: #ebebeb;
            }
          }

          &-price {
            color: #0b0b0b;
            font-size: 18px;
            font-weight: 600;

            &.is-active {
              color: #cdcecd;
              font-size: 14px;
              font-weight: 400;
              text-decoration: line-through;
            }
          }

          &-strike {
            padding: 0 10px;

            color: #cdcecd;
            font-size: 14px;
            font-weight: 400;
          }

          &-special {
            color: #0b0b0b;
            font-size: 18px;
            font-weight: 600;
          }

          &.disabled {
            margin-top: 0;
          }
        }
      }

      &-addtocart {
        width: 100%;
        background-color: #11bb2e;

        &:hover {
          background-color: rgba(17, 187, 46, 0.8)
        }
      }
    }
  }

  &-info {
    margin-top: 30px;

    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    @media only screen and (max-width: 576px) {
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
    }

    &-title {
      color: #111;
      font-size: 18px;
      font-weight: 600;
    }

    &-table {
      width: 100%;

      @media only screen and (max-width: 576px) {
        margin-bottom: 20px;
      }

      & tr {
        width: 100%;

        &:nth-child(odd) {
          background: #f9f8f6;
        }

        & td {
          width: 50%;
          padding: 7px;

          color: #333;
          font-size: 12px;
          font-weight: 400;
        }
      }
    }

    &-wrapper {
      margin-top: 10px;
      height: 186px;
      overflow-y: scroll;

      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-track {
        border-radius: 2px;
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        width: 2px;
        border-radius: 5px;
        background: #11bb2e;
      }
    }

    &-text {
      margin: 0;
      padding: 0 10px 10px 10px;

      color: #111;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.5;
    }
  }

  &-slider {
    margin-top: 30px;
    position: relative;
  }
}
</style>
<style lang="scss">
.product-modal {
  &-content {
    &-details {
      &-qty {
        &-container {
          .price {
            margin: 0;

            > div {
              align-items: center;
              flex-direction: row-reverse;

              .price-original {
                font-size: 16px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
